import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ApiRequest, AuthType, HttpMethod, RequestBody } from '@core/entities/ApiRequest';
import type { RequestSettings } from '@core/entities/RequestSettings';
import { STORAGE_KEYS } from '@shared/constants';

/** The editable, in-memory form of a request — a saved request's server fields stripped away. */
export interface DraftRequest {
  name: string;
  method: HttpMethod;
  url: string;
  description: string | null;
  authType: AuthType;
  authConfig: Record<string, unknown> | null;
  preRequestScript: string | null;
  testScript: string | null;
  settings: RequestSettings;
  params: ApiRequest['params'];
  headers: ApiRequest['headers'];
  body: RequestBody | null;
  variables: ApiRequest['variables'];
}

export interface WorkbenchTab {
  key: string;
  workspaceId: string;
  collectionId: string;
  folderId: string | null;
  /** null until the draft has been saved for the first time. */
  requestId: string | null;
  draft: DraftRequest;
  isDirty: boolean;
  isPinned: boolean;
}

const toDraft = (request: ApiRequest): DraftRequest => ({
  name: request.name,
  method: request.method,
  url: request.url,
  description: request.description,
  authType: request.authType,
  authConfig: request.authConfig,
  preRequestScript: request.preRequestScript,
  testScript: request.testScript,
  settings: request.settings,
  params: request.params,
  headers: request.headers,
  body: request.body,
  variables: request.variables,
});

export const blankDraft = (): DraftRequest => ({
  name: 'Untitled Request',
  method: 'GET',
  url: '',
  description: null,
  authType: 'inherit',
  authConfig: null,
  preRequestScript: null,
  testScript: null,
  settings: {},
  params: [],
  headers: [],
  body: null,
  variables: [],
});

const MAX_CLOSED_TABS = 10;

interface WorkbenchState {
  tabs: WorkbenchTab[];
  activeKey: string | null;
  /** Most-recently-closed first, capped at MAX_CLOSED_TABS. */
  closedTabs: WorkbenchTab[];
  openRequestTab: (workspaceId: string, request: ApiRequest) => void;
  openNewRequestTab: (workspaceId: string, collectionId: string, folderId: string | null) => void;
  closeTab: (key: string) => void;
  closeOtherTabs: (key: string) => void;
  closeAllTabs: () => void;
  setActiveKey: (key: string) => void;
  updateDraft: (key: string, patch: Partial<DraftRequest>) => void;
  markSaved: (key: string, saved: ApiRequest) => void;
  /**
   * Re-syncs the name of every open tab pointing at `requestId` — needed
   * because a rename from the sidebar (or anywhere outside the tab itself)
   * only updates the React Query cache, not this separate Zustand store. A
   * tab left unsynced still holds the pre-rename name in its draft, and
   * hitting Save on it would silently overwrite the rename back to the old
   * name in the database.
   */
  syncTabName: (requestId: string, name: string) => void;
  togglePin: (key: string) => void;
  /** Opens a copy of the tab's current draft in a new, unsaved tab — a local copy, not a server-side duplicate. */
  duplicateTab: (key: string) => void;
  reopenLastClosedTab: () => void;
  reorderTabs: (orderedKeys: string[]) => void;
}

export const useWorkbenchStore = create<WorkbenchState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeKey: null,
      closedTabs: [],

      openRequestTab: (workspaceId, request) => {
        const existing = get().tabs.find((tab) => tab.requestId === request.id);
        if (existing) {
          // Re-syncs the name even for an already-open tab: the tree only
          // fetches a fresh entity when it wasn't already open (see
          // RequestTreeNode.handleOpen), so this is the one place a rename
          // that happened while the tab was open (and hadn't yet round-tripped
          // through syncTabName) gets picked up on next open.
          set((state) => ({
            activeKey: existing.key,
            tabs: state.tabs.map((tab) =>
              tab.key === existing.key
                ? { ...tab, draft: { ...tab.draft, name: request.name } }
                : tab,
            ),
          }));
          return;
        }

        const tab: WorkbenchTab = {
          key: crypto.randomUUID(),
          workspaceId,
          collectionId: request.collectionId,
          folderId: request.folderId,
          requestId: request.id,
          draft: toDraft(request),
          isDirty: false,
          isPinned: false,
        };

        set((state) => ({ tabs: [...state.tabs, tab], activeKey: tab.key }));
      },

      openNewRequestTab: (workspaceId, collectionId, folderId) => {
        const tab: WorkbenchTab = {
          key: crypto.randomUUID(),
          workspaceId,
          collectionId,
          folderId,
          requestId: null,
          draft: blankDraft(),
          isDirty: false,
          isPinned: false,
        };

        set((state) => ({ tabs: [...state.tabs, tab], activeKey: tab.key }));
      },

      closeTab: (key) => {
        set((state) => {
          const closing = state.tabs.find((tab) => tab.key === key);
          const remaining = state.tabs.filter((tab) => tab.key !== key);
          const wasActive = state.activeKey === key;
          const activeKey = wasActive
            ? (remaining[remaining.length - 1]?.key ?? null)
            : state.activeKey;
          const closedTabs = closing
            ? [closing, ...state.closedTabs].slice(0, MAX_CLOSED_TABS)
            : state.closedTabs;
          return { tabs: remaining, activeKey, closedTabs };
        });
      },

      closeOtherTabs: (key) => {
        set((state) => {
          const staying = state.tabs.filter((tab) => tab.key === key || tab.isPinned);
          const closing = state.tabs.filter((tab) => tab.key !== key && !tab.isPinned);
          return {
            tabs: staying,
            activeKey: key,
            closedTabs: [...closing.reverse(), ...state.closedTabs].slice(0, MAX_CLOSED_TABS),
          };
        });
      },

      closeAllTabs: () => {
        set((state) => {
          const staying = state.tabs.filter((tab) => tab.isPinned);
          const closing = state.tabs.filter((tab) => !tab.isPinned);
          return {
            tabs: staying,
            activeKey: staying[0]?.key ?? null,
            closedTabs: [...closing.reverse(), ...state.closedTabs].slice(0, MAX_CLOSED_TABS),
          };
        });
      },

      setActiveKey: (key) => set({ activeKey: key }),

      updateDraft: (key, patch) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.key === key ? { ...tab, draft: { ...tab.draft, ...patch }, isDirty: true } : tab,
          ),
        }));
      },

      markSaved: (key, saved) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.key === key
              ? {
                  ...tab,
                  requestId: saved.id,
                  collectionId: saved.collectionId,
                  folderId: saved.folderId,
                  draft: toDraft(saved),
                  isDirty: false,
                }
              : tab,
          ),
        }));
      },

      syncTabName: (requestId, name) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.requestId === requestId ? { ...tab, draft: { ...tab.draft, name } } : tab,
          ),
        }));
      },

      togglePin: (key) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.key === key ? { ...tab, isPinned: !tab.isPinned } : tab,
          ),
        }));
      },

      duplicateTab: (key) => {
        set((state) => {
          const index = state.tabs.findIndex((tab) => tab.key === key);
          const source = state.tabs[index];
          if (!source) return state;

          const duplicate: WorkbenchTab = {
            ...source,
            key: crypto.randomUUID(),
            requestId: null,
            isPinned: false,
            isDirty: true,
            draft: { ...source.draft, name: `${source.draft.name} (Copy)` },
          };

          const tabs = [
            ...state.tabs.slice(0, index + 1),
            duplicate,
            ...state.tabs.slice(index + 1),
          ];
          return { tabs, activeKey: duplicate.key };
        });
      },

      reopenLastClosedTab: () => {
        set((state) => {
          const [lastClosed, ...rest] = state.closedTabs;
          if (!lastClosed) return state;

          const restored: WorkbenchTab = { ...lastClosed, key: crypto.randomUUID() };
          return { tabs: [...state.tabs, restored], activeKey: restored.key, closedTabs: rest };
        });
      },

      reorderTabs: (orderedKeys) => {
        set((state) => {
          const byKey = new Map(state.tabs.map((tab) => [tab.key, tab]));
          const tabs = orderedKeys
            .map((key) => byKey.get(key))
            .filter((tab): tab is WorkbenchTab => Boolean(tab));
          return tabs.length === state.tabs.length ? { tabs } : state;
        });
      },
    }),
    { name: STORAGE_KEYS.workbenchTabs },
  ),
);
